import { ArrowRight, Calendar, Check, Clock, Filter, Layers, RefreshCw } from 'lucide-react'
import type React from 'react'
import { MaterialCard } from '../ui/MaterialCard'
import type { InspectionMode, InspectionSource } from './types'

interface InspectorControlsProps {
    mode: InspectionMode
    setMode: (mode: InspectionMode) => void
    inspectionSource: InspectionSource
    setInspectionSource: (source: InspectionSource) => void
    loadOpfsDebugData?: () => void
    rangeStartDate: string
    setRangeStartDate: (date: string) => void
    rangeEndDate: string
    setRangeEndDate: (date: string) => void
    onReset?: () => void
    baseAnalysis?: any
    children?: React.ReactNode
}

export const InspectorControls: React.FC<InspectorControlsProps> = ({
    mode,
    setMode,
    inspectionSource,
    setInspectionSource,
    loadOpfsDebugData,
    rangeStartDate,
    setRangeStartDate,
    rangeEndDate,
    setRangeEndDate,
    onReset,
    baseAnalysis,
    children,
}) => {
    const needsDateRange = inspectionSource === 'RangeIntraday' || inspectionSource === 'RangeEOD'

    return (
        <MaterialCard className="border border-slate-200/60 dark:border-slate-800/60 shadow-md bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm flex flex-col p-3 md:p-4 text-left transition-colors duration-300 rounded-[20px]">
            <div className="flex flex-col xl:flex-row gap-4 xl:items-center w-full">
                {/* Header Section (Compact) */}
                <div className="flex items-center justify-between xl:justify-start gap-4 xl:w-auto shrink-0 border-b xl:border-b-0 xl:border-r border-slate-100 dark:border-slate-800 pb-3 xl:pb-0 xl:pr-6">
                    <div className="flex items-center gap-3">
                        <div className="p-1.5 bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400 rounded-md">
                            <Filter size={16} strokeWidth={2.5} />
                        </div>
                        <div>
                            <h3 className="text-xs font-black text-slate-800 dark:text-slate-100 uppercase tracking-widest hidden sm:block">
                                Inspector Parameters
                            </h3>
                            <h3 className="text-xs font-black text-slate-800 dark:text-slate-100 uppercase tracking-widest sm:hidden">
                                Parameters
                            </h3>
                        </div>
                    </div>

                    {onReset && (
                        <button
                            onClick={onReset}
                            className="flex items-center justify-center p-1.5 text-slate-500 hover:text-indigo-600 dark:text-slate-400 dark:hover:text-indigo-400 bg-slate-50 dark:bg-slate-800/50 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded-md transition-colors w-7 h-7 xl:w-auto xl:h-auto xl:px-2 xl:gap-1.5 border border-slate-200 dark:border-slate-700"
                            title="Reset Defaults"
                        >
                            <RefreshCw size={12} className="shrink-0" />
                            <span className="text-[10px] font-bold hidden xl:inline">Reset</span>
                        </button>
                    )}

                    {/* Base Time Reference Chips (NEW - Moved here for alignment) */}
                    {baseAnalysis && (
                        <div className="flex items-center gap-2.5 px-3 py-1.5 rounded-xl bg-slate-50/50 dark:bg-slate-800/30 border border-slate-200/50 dark:border-slate-700/40 shadow-sm animate-in fade-in slide-in-from-left-2 duration-500">
                            {/* Base R1 (Resistance) - GREEN as requested */}
                            <div className="flex items-center gap-2">
                                <span className="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest leading-none">
                                    Base R1
                                </span>
                                <span className="text-xs font-black premium-cell-mono leading-none text-emerald-500 dark:text-emerald-400">
                                    {baseAnalysis.resistanceStrike || '—'}
                                </span>
                            </div>

                            {/* Divider */}
                            <div className="w-px h-3.5 bg-slate-200/60 dark:bg-slate-700/60 mx-0.5"></div>

                            {/* Base S1 (Support) - RED as requested */}
                            <div className="flex items-center gap-2">
                                <span className="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest leading-none">
                                    Base S1
                                </span>
                                <span className="text-xs font-black premium-cell-mono leading-none text-rose-500 dark:text-rose-400">
                                    {baseAnalysis.supportStrike || '—'}
                                </span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Main Controls Wrapper */}
                <div className="flex flex-col lg:flex-row gap-4 grow min-w-0 items-start lg:items-center justify-between">
                    {/* Controls Row 1: Mode & Source */}
                    <div className="flex flex-col sm:flex-row gap-4 w-full lg:w-auto items-start sm:items-center min-w-0">
                        {/* 1. Mode Selection */}
                        <div className="flex items-center gap-2 shrink-0">
                            <span className="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest hidden sm:block w-12 text-right">
                                Mode
                            </span>
                            <div className="flex p-0.5 bg-slate-100/80 dark:bg-slate-800/80 rounded-lg border border-slate-200/50 dark:border-slate-700/50">
                                {[
                                    { id: 'Single', icon: Filter, label: 'Single' },
                                    { id: 'Multiple', icon: Layers, label: 'Dual' },
                                    { id: 'Custom', icon: Check, label: 'Custom' },
                                ].map((m) => (
                                    <button
                                        key={m.id}
                                        onClick={() => setMode(m.id as InspectionMode)}
                                        className={`flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-[10px] font-bold transition-all duration-300 ${
                                            mode === m.id
                                                ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-300 shadow-sm ring-1 ring-black/5 dark:ring-white/10'
                                                : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-200/50 dark:hover:bg-slate-700/50'
                                        }`}
                                    >
                                        <m.icon size={11} strokeWidth={3} className="hidden sm:inline" /> {m.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Divider */}
                        <div className="hidden sm:block w-px h-6 bg-slate-200 dark:bg-slate-700 mx-1 shrink-0"></div>

                        {/* 2. Source Selection */}
                        <div className="flex items-center gap-2 min-w-0 overflow-x-auto pb-1 sm:pb-0 w-full no-scrollbar">
                            <span className="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest hidden sm:block w-12 text-right shrink-0">
                                Source
                            </span>
                            <div className="flex gap-1.5 w-max">
                                {[
                                    { id: 'CurrentIntraday', label: 'Live' },
                                    { id: 'HistoricalSnapshot', label: 'Historic' },
                                    { id: 'RangeIntraday', label: 'Multi' },
                                ].map((item) => (
                                    <button
                                        key={item.id}
                                        onClick={() => {
                                            setInspectionSource(item.id as InspectionSource)
                                        }}
                                        className={`px-3 py-1.5 rounded-lg text-[10px] font-bold border transition-all ${
                                            inspectionSource === item.id
                                                ? 'bg-indigo-50 dark:bg-indigo-900/30 border-indigo-200 dark:border-indigo-800 text-indigo-700 dark:text-indigo-300 shadow-inner'
                                                : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:border-slate-300 dark:hover:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-750'
                                        }`}
                                    >
                                        {item.label}
                                    </button>
                                ))}
                                {/* OPFS Debug Option (Rarely Used, pushed to end) */}
                                <button
                                    onClick={() => {
                                        setInspectionSource('OPFSDebug')
                                        loadOpfsDebugData?.()
                                    }}
                                    className={`px-2 py-1.5 rounded-lg text-[10px] font-bold border transition-all truncate ${
                                        inspectionSource === 'OPFSDebug'
                                            ? 'bg-rose-50 dark:bg-rose-900/20 border-rose-200 dark:border-rose-800 text-rose-600 dark:text-rose-400'
                                            : 'bg-transparent border-transparent text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                                    }`}
                                    title="OPFS Debug"
                                >
                                    OPFS
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Controls Row 2: Date Range (Only if needed) */}
                    {needsDateRange && (
                        <div className="flex items-center gap-2 w-full lg:w-auto shrink-0 animate-in fade-in slide-in-from-right-4 duration-300 border-t lg:border-t-0 lg:border-l border-slate-100 dark:border-slate-800 pt-3 lg:pt-0 lg:pl-4">
                            <Calendar
                                size={14}
                                className="text-indigo-400 dark:text-indigo-500 shrink-0 hidden sm:block"
                            />
                            <div className="flex items-center gap-1.5 w-full bg-slate-50/80 dark:bg-slate-800/50 p-1 rounded-lg border border-slate-200/50 dark:border-slate-700/50">
                                <input
                                    type="date"
                                    value={rangeStartDate}
                                    onChange={(e) => setRangeStartDate(e.target.value)}
                                    className="w-full sm:w-[110px] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded px-2 py-1 text-[10px] font-bold text-slate-700 dark:text-slate-200 focus:outline-none focus:border-indigo-400 dark:scheme-dark transition-colors"
                                />
                                <ArrowRight size={10} className="text-slate-300 dark:text-slate-600 shrink-0" />
                                <input
                                    type="date"
                                    value={rangeEndDate}
                                    onChange={(e) => setRangeEndDate(e.target.value)}
                                    className="w-full sm:w-[110px] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded px-2 py-1 text-[10px] font-bold text-slate-700 dark:text-slate-200 focus:outline-none focus:border-indigo-400 dark:scheme-dark transition-colors"
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {children && <div className="mt-3">{children}</div>}
        </MaterialCard>
    )
}
